import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import {ChevronDown} from 'lucide-react'

const FAQ = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  })

  const [openIndex, setOpenIndex] = useState<number | null>(0)

  const faqs = [
    {
      question: 'Preciso começar do zero?',
      answer: 'Não. Você escolhe o nível ou objetivo mais adequado ao seu momento. Temos conteúdo do básico ao avançado.',
    },
    {
      question: 'Quanto tempo por dia preciso estudar?',
      answer: 'O EW Pass se adapta à sua rotina. A constância importa mais do que longas horas. Mesmo 15-20 minutos diários fazem diferença quando há estrutura e apoio.',
    },
    {
      question: 'Já tentei aprender inglês antes. Funciona pra mim?',
      answer: 'Sim. O modelo foi criado justamente para quem já tentou sozinho e não conseguiu manter. O diferencial é o suporte contínuo: professores, IA 24h e comunidade.',
    },
    {
      question: 'Posso cancelar?',
      answer: 'Sim. As regras de cancelamento são claras e sem burocracia. Você tem total liberdade para pausar ou cancelar sua assinatura.',
    },
    {
      question: 'Como funciona a garantia de 6 meses?',
      answer: 'Seguindo a metodologia proposta (assistindo aulas, tirando dúvidas, praticando), se não houver evolução percebida, você pode solicitar o reembolso conforme contrato.',
    },
  ]

  return (
    <section ref={ref} className="py-20 md:py-32 bg-gradient-to-b from-white to-gray-50">
      <div className="container mx-auto px-4 md:px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-gray-900 mb-6 leading-tight">
              Dúvidas{' '}
              <span className="bg-gradient-to-r from-[#0D7FA3] to-[#C64DAD] bg-clip-text text-transparent">
                frequentes
              </span>
            </h2>
            <p className="text-xl text-gray-600">
              Tudo o que você precisa saber antes de começar
            </p>
          </motion.div>

          <div className="space-y-4">
            {faqs.map((faq, index) => {
              const isOpen = openIndex === index

              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <motion.button
                    onClick={() => setOpenIndex(isOpen ? null : index)}
                    whileHover={{ scale: 1.01 }}
                    className={`
                      w-full text-left bg-white rounded-2xl p-6 md:p-8 shadow-md hover:shadow-lg transition-all duration-300
                      ${isOpen ? 'ring-2 ring-[#0D7FA3]' : 'border-2 border-gray-100'}
                    `}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <h3 className="text-lg md:text-xl font-bold text-gray-900 pr-4">
                        {faq.question}
                      </h3>
                      <motion.div
                        animate={{ rotate: isOpen ? 180 : 0 }}
                        transition={{ duration: 0.3 }}
                        className="flex-shrink-0"
                      >
                        <ChevronDown className="w-6 h-6 text-[#0D7FA3]" strokeWidth={2.5} />
                      </motion.div>
                    </div>

                    <AnimatePresence>
                      {isOpen && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          className="overflow-hidden"
                        >
                          <div className="pt-4 border-t border-gray-200 mt-4">
                            <p className="text-base md:text-lg text-gray-600 leading-relaxed">
                              {faq.answer}
                            </p>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.button>
                </motion.div>
              )
            })}
          </div>

          {/* Bottom CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="mt-12 text-center bg-gradient-to-r from-[#0D7FA3]/10 to-[#C64DAD]/10 rounded-2xl p-8 border-2 border-[#0D7FA3]/20"
          >
            <p className="text-xl md:text-2xl text-gray-900 font-bold mb-4">
              Ainda tem dúvidas?
            </p>
            <p className="text-lg text-gray-600 mb-6">
              Experimente o EW Pass com nossa garantia de 6 meses. Sem riscos.
            </p>
            <motion.button
              onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-gradient-to-r from-[#0D7FA3] to-[#C64DAD] text-white font-bold text-lg px-10 py-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
            >
              Ver planos
            </motion.button>
          </motion.div>
        </div>
      </div>
    </section>
  )
}

export default FAQ
